import { memo, useCallback, useEffect } from 'react';
import './GalleryPagination.css';

// ==========================================
// TYPES
// ==========================================

type PaginationVariant = 'arrows' | 'dots' | 'numbered' | 'minimal';

interface GalleryPaginationProps {
  currentPage: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onGoToPage?: (page: number) => void;
  isLoading?: boolean;
  variant?: PaginationVariant;
  showPageInfo?: boolean;
  enableKeyboard?: boolean;
  className?: string;
}

// ==========================================
// COMPONENT
// ==========================================

function GalleryPaginationComponent({
  currentPage,
  totalPages,
  hasNextPage,
  hasPreviousPage,
  onPrevious,
  onNext,
  onGoToPage,
  isLoading = false,
  variant = 'arrows',
  showPageInfo = true,
  enableKeyboard = true,
  className = '',
}: GalleryPaginationProps) {
  // Keyboard navigation
  useEffect(() => {
    if (!enableKeyboard) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && hasPreviousPage && !isLoading) {
        onPrevious();
      } else if (e.key === 'ArrowRight' && hasNextPage && !isLoading) {
        onNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enableKeyboard, hasPreviousPage, hasNextPage, isLoading, onPrevious, onNext]);

  // Generate page numbers for numbered variant
  const getPageNumbers = useCallback(() => {
    const pages: (number | 'ellipsis')[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Always show first page
    pages.push(1);

    // Calculate range around current page
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);

    // Adjust if at the edges
    if (currentPage <= 2) {
      end = Math.min(totalPages - 1, 4);
    } else if (currentPage >= totalPages - 1) {
      start = Math.max(2, totalPages - 3);
    }

    // Add ellipsis if needed
    if (start > 2) {
      pages.push('ellipsis');
    }

    // Add middle pages
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // Add ellipsis if needed
    if (end < totalPages - 1) {
      pages.push('ellipsis');
    }

    // Always show last page
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  }, [currentPage, totalPages]);

  if (totalPages <= 1) {
    return null;
  }

  // ==========================================
  // MINIMAL VARIANT
  // ==========================================

  if (variant === 'minimal') {
    return (
      <div className={`gallery-pagination gallery-pagination--minimal ${className}`}>
        <button
          className="gallery-pagination__btn gallery-pagination__btn--prev"
          onClick={onPrevious}
          disabled={!hasPreviousPage || isLoading}
          aria-label="Previous page"
        >
          ←
        </button>
        <span className="gallery-pagination__info">
          {currentPage} / {totalPages}
        </span>
        <button
          className="gallery-pagination__btn gallery-pagination__btn--next"
          onClick={onNext}
          disabled={!hasNextPage || isLoading}
          aria-label="Next page"
        >
          →
        </button>
      </div>
    );
  }

  // ==========================================
  // DOTS VARIANT
  // ==========================================

  if (variant === 'dots') {
    const maxDots = 7;
    const dots: number[] = [];

    if (totalPages <= maxDots) {
      for (let i = 1; i <= totalPages; i++) {
        dots.push(i);
      }
    } else {
      // Show dots around current page
      let start = Math.max(1, currentPage - Math.floor(maxDots / 2));
      const end = Math.min(totalPages, start + maxDots - 1);
      start = Math.max(1, end - maxDots + 1);

      for (let i = start; i <= end; i++) {
        dots.push(i);
      }
    }

    return (
      <div className={`gallery-pagination gallery-pagination--dots ${className}`}>
        <button
          className="gallery-pagination__btn gallery-pagination__btn--prev"
          onClick={onPrevious}
          disabled={!hasPreviousPage || isLoading}
          aria-label="Previous page"
        >
          ‹
        </button>

        <div className="gallery-pagination__dots">
          {dots.map((page) => (
            <button
              key={page}
              className={`gallery-pagination__dot ${page === currentPage ? 'active' : ''}`}
              onClick={() => onGoToPage?.(page)}
              disabled={isLoading}
              aria-label={`Go to page ${page}`}
              aria-current={page === currentPage ? 'page' : undefined}
            />
          ))}
        </div>

        <button
          className="gallery-pagination__btn gallery-pagination__btn--next"
          onClick={onNext}
          disabled={!hasNextPage || isLoading}
          aria-label="Next page"
        >
          ›
        </button>
      </div>
    );
  }

  // ==========================================
  // NUMBERED VARIANT
  // ==========================================

  if (variant === 'numbered') {
    const pageNumbers = getPageNumbers();

    return (
      <div className={`gallery-pagination gallery-pagination--numbered ${className}`}>
        <button
          className="gallery-pagination__btn gallery-pagination__btn--prev"
          onClick={onPrevious}
          disabled={!hasPreviousPage || isLoading}
          aria-label="Previous page"
        >
          ‹ Prev
        </button>

        <div className="gallery-pagination__numbers">
          {pageNumbers.map((page, index) =>
            page === 'ellipsis' ? (
              <span key={`ellipsis-${index}`} className="gallery-pagination__ellipsis">
                …
              </span>
            ) : (
              <button
                key={page}
                className={`gallery-pagination__number ${page === currentPage ? 'active' : ''}`}
                onClick={() => onGoToPage?.(page)}
                disabled={isLoading}
                aria-label={`Go to page ${page}`}
                aria-current={page === currentPage ? 'page' : undefined}
              >
                {page}
              </button>
            )
          )}
        </div>

        <button
          className="gallery-pagination__btn gallery-pagination__btn--next"
          onClick={onNext}
          disabled={!hasNextPage || isLoading}
          aria-label="Next page"
        >
          Next ›
        </button>
      </div>
    );
  }

  // ==========================================
  // ARROWS VARIANT (Default)
  // ==========================================

  return (
    <div className={`gallery-pagination gallery-pagination--arrows ${className}`}>
      <button
        className="gallery-pagination__btn gallery-pagination__btn--prev gallery-pagination__btn--large"
        onClick={onPrevious}
        disabled={!hasPreviousPage || isLoading}
        aria-label="Previous page"
      >
        <span className="gallery-pagination__arrow">←</span>
        <span className="gallery-pagination__btn-text">Previous</span>
      </button>

      {showPageInfo && (
        <div className="gallery-pagination__page-info">
          <span className="gallery-pagination__current">{currentPage}</span>
          <span className="gallery-pagination__separator">of</span>
          <span className="gallery-pagination__total">{totalPages}</span>
        </div>
      )}

      <button
        className="gallery-pagination__btn gallery-pagination__btn--next gallery-pagination__btn--large"
        onClick={onNext}
        disabled={!hasNextPage || isLoading}
        aria-label="Next page"
      >
        <span className="gallery-pagination__btn-text">Next</span>
        <span className="gallery-pagination__arrow">→</span>
      </button>

      {isLoading && (
        <div className="gallery-pagination__loading">
          <span className="gallery-pagination__spinner" />
        </div>
      )}
    </div>
  );
}

export const GalleryPagination = memo(GalleryPaginationComponent);
